// Schedule states as the schedule payload lists them. Only `scheduled` activities have a float.
export const SCHEDULE_SCHEDULED = 'scheduled';
export const SCHEDULE_NO_LINKS = 'no_links';
export const SCHEDULE_NOT_LINKED_TO_TARGET = 'not_linked_to_target';
export const SCHEDULE_COMPLETE = 'complete';

/** Each activity in the schedule payload, keyed by id, as `{ state, float, earlyStart }`. */
export const scheduleEntries = (schedule) => {
    const entries = new Map();

    if (!schedule) {
        return entries;
    }

    Object.entries(schedule.floats || {}).forEach(([id, float]) => {
        entries.set(Number(id), { state: SCHEDULE_SCHEDULED, float, earlyStart: schedule.early_starts?.[id] ?? null });
    });

    [SCHEDULE_NO_LINKS, SCHEDULE_NOT_LINKED_TO_TARGET, SCHEDULE_COMPLETE].forEach((state) => {
        (schedule[state] || []).forEach((id) => entries.set(Number(id), { state }));
    });

    return entries;
};

export const isCriticalEntry = (entry) => entry?.state === SCHEDULE_SCHEDULED && entry.float === 0;

/**
 * How an activity's float reads, as `{ text, muted, ariaLabel }`, or null without an entry. `muted`
 * marks a reason for having no float; `ariaLabel` is set when `text` alone says nothing.
 */
export const floatLabel = (entry, hasTarget) => {
    if (!entry) {
        return null;
    }

    if (entry.state === SCHEDULE_COMPLETE) {
        return { text: $t('Complete'), muted: true };
    }

    if (!hasTarget) {
        return { text: '—', muted: true, ariaLabel: $t('No target') };
    }

    if (entry.state === SCHEDULE_NO_LINKS) {
        return { text: $t('No links'), muted: true };
    }

    if (entry.state === SCHEDULE_NOT_LINKED_TO_TARGET) {
        return { text: $t('Not linked to target'), muted: true };
    }

    return { text: floatDaysLabel(entry.float), muted: false };
};

/** A float in days: "0 days", "1 day", "3 days". */
export const floatDaysLabel = (float) => (float === 1 ? $t('1 day') : $t('{{count}} days', { count: float }));

/** The description of the key milestone the schedule was calculated against, or null. */
export const targetName = (schedule) => schedule?.key_milestones?.find((milestone) => milestone.id === schedule.target_id)?.description ?? null;
